import { useEffect, useRef } from 'react';
import { APP_VERSION, API_BASE_URL } from '../shared/constants';

/**
 * Controlla periodicamente e al cambio di visibilità (ritorno su app/sblocco smartphone)
 * se il server è stato aggiornato a una nuova versione.
 * Se viene rilevata una nuova versione o il Service Worker si è aggiornato:
 * - Se la squadra non sta digitando in un input/report, ricarica automaticamente l'app.
 * - Il token di sessione in localStorage è preservato: NESSUN logout necessario!
 */
export function useVersionChecker() {
  const isCheckingRef = useRef(false);

  useEffect(() => {
    async function check() {
      if (isCheckingRef.current) return;
      isCheckingRef.current = true;
      try {
        const res = await fetch(`${API_BASE_URL}/version?_t=${Date.now()}`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (data.version && data.version !== APP_VERSION) {
            console.log(`[Road Hub Squadra] Nuova versione ${data.version} rilevata (attuale: ${APP_VERSION}). Auto-aggiornamento...`);
            const activeEl = document.activeElement;
            const isTyping = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || (activeEl as HTMLElement).isContentEditable);
            if (!isTyping) {
              window.location.reload();
            }
          }
        }
      } catch {
        // Ignora offline momentaneo
      } finally {
        isCheckingRef.current = false;
      }
    }

    // Controlla ogni 45 secondi
    const interval = setInterval(check, 45_000);

    // Controlla al ritorno sull'app / sblocco display
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Rileva l'attivazione del nuovo Service Worker PWA solo se c'era già un controller precedente
    let refreshing = false;
    const handleControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      console.log('[Road Hub Squadra] Service Worker aggiornato dal deploy. Ricaricamento client...');
      const activeEl = document.activeElement;
      const isTyping = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA');
      if (!isTyping) {
        window.location.reload();
      }
    };

    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    }

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      }
    };
  }, []);
}
